import { Job } from '../../../entities/job/job';
import { ActivityEntityName } from '../../../entities/shared/activity.entity.name';
import { EntitySecurityCapabilityRegistry } from '../../entity.security.capability.registry';
import { EntitySecurityFacts } from '../../entity.security.facts';
import { JobSecurityRight } from '../../modules/job.security.module';
import { JobSecurityCapabilities } from './job.security.capabilities';
import { jobSecurityHasRight } from './job.security.has.right';
import { jobIsClosedOrCancelled, jobIsCompletedOrLater, jobIsFinalised } from './job.security.state';

EntitySecurityCapabilityRegistry.register<Job, JobSecurityCapabilities>(ActivityEntityName.Job, getJobSecurityCapabilities);

export function getJobSecurityCapabilities(facts: EntitySecurityFacts<Job>): JobSecurityCapabilities {

    const job = facts.before ?? facts.entity ?? facts.after;
    const role = facts.role;
    const canManage = jobSecurityHasRight(role, JobSecurityRight.Manage);
    const canModifyFinalised = jobSecurityHasRight(role, JobSecurityRight.ModifyJobAfterFinalise);

    return {
        canManage,
        // Closed family is a hard state lock for everyone; Finalised is permission-gated (WBS-10241),
        // mirroring the stage patch model (WBS-10277).
        canPatch: canManage && !jobIsClosedOrCancelled(job) && (!jobIsFinalised(job) || canModifyFinalised),
        // Notes are commentary, not record edits: a Finalised job still accepts them without the
        // Modify-after-finalise right; a Closed job blocks everything (WBS-10241 ruling).
        canAddNote: !jobIsClosedOrCancelled(job),
        canDelete: canManage && (!jobIsFinalised(job) || canModifyFinalised),
        canDispatch: jobSecurityHasRight(role, JobSecurityRight.Dispatch),
        canProcessStatus: jobSecurityHasRight(role, JobSecurityRight.ProcessJob),
        canFinalise: jobSecurityHasRight(role, JobSecurityRight.FinaliseJob),
        canViewPricing: jobSecurityHasRight(role, JobSecurityRight.ViewPricing, JobSecurityRight.ModifyPricing),
        canModifyPricing: jobSecurityHasRight(role, JobSecurityRight.ModifyPricing),
        canModifyFinalised,
        canModifyCompletionDate: jobIsCompletedOrLater(job) && jobSecurityHasRight(role, JobSecurityRight.CompletionDate),
        canModifyFinaliseDate: jobIsFinalised(job) && jobSecurityHasRight(role, JobSecurityRight.FinaliseDate),
        canMarkFinanceLock: jobSecurityHasRight(role, JobSecurityRight.MarkFinanceLock),
        canChangeStructure: canManage && jobSecurityHasRight(role, JobSecurityRight.ChangeJobStructure),
    };
}
